/* eslint-disable react/no-unstable-nested-components */

/* eslint-disable react/forbid-prop-types */
import classNames from 'classnames';
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';
import React, { Fragment, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useEdition } from '../../contexts/EditionContext';
import { useUrlFromHandle } from '../../contexts/RoutesContext';
import AddToFavoritesButton from '../buttons/AddToFavorites';
import EventTicketsButton from '../buttons/EventTickets';
import SmallTicketIcon from '../icons/SmallTicket';
import Body from './Body';
import EventDates from './EventDates';
import IconLink from './IconLink';
import Link from './Link';

import styles from '../../../styles/partials/booking-details.module.scss';

const propTypes = {
    events: PropTypes.arrayOf(PropTypes.object).isRequired,
    className: PropTypes.string,
    isDark: PropTypes.bool,
    buttonIsDark: PropTypes.bool,
};

const defaultProps = {
    className: null,
    isDark: false,
    buttonIsDark: false,
};

const BookingDetails = ({ events, className, isDark, buttonIsDark }) => {
    const routeByHandle = useUrlFromHandle();
    const ticketsRoute = routeByHandle('tickets');

    const { tickets = [] } = useEdition() || {};
    const hasEditionTickets = events.reduce(
        (has, { isOnline, type }) => has || (!isOnline && type === 'screening'),
        false,
    );
    const isVirtualReality = events.reduce(
        (has, { venue = null }) => has || venue?.id === '3',
        false,
    );
    const isExplore = events.reduce(
        (has, { film }) =>
            has ||
            ((film?.sections || []).find(({ slug = null }) => slug === 'explore') || null) !== null,
        false,
    );
    const editionTickets = tickets.filter(({ type }) => type !== 'forum');

    const initialEvents = useMemo(
        () => sortBy(events || [], ({ startDate = null }) => startDate),
        [events],
    );

    const groupedByVenue = useMemo(
        () =>
            sortBy(
                initialEvents.reduce((acc, event) => {
                    const {
                        venue: eventVenue = null,
                        isOnline: eventIsOnline = false,
                        isPurchasable: eventIsPurchasable = true,
                    } = event;
                    const { id: eventVenueId = null } = eventVenue || {};

                    const index = acc.findIndex(
                        ({ venue: sectionVenue = null, isOnline: sectionIsOnline = false }) => {
                            const { id: sectionVenueId = null } = sectionVenue || {};
                            return (
                                (eventVenueId !== null &&
                                    sectionVenueId !== null &&
                                    eventVenueId === sectionVenueId) ||
                                (eventVenueId === null &&
                                    sectionVenueId === null &&
                                    eventIsOnline &&
                                    sectionIsOnline)
                            );
                        },
                    );

                    if (index === -1) {
                        return [
                            ...acc,
                            {
                                id: eventVenue !== null ? eventVenue.id : acc.length,
                                venue: eventVenue,
                                isOnline: eventIsOnline,
                                isPurchasable: eventIsPurchasable,
                                startDate: event?.startDate,
                                events: [event],
                            },
                        ];
                    }
                    return [
                        ...acc.slice(0, index),
                        {
                            ...acc[index],
                            events: [...acc[index].events, event],
                        },
                        ...acc.slice(index + 1),
                    ];
                }, []),
                ['startDate', 'isOnline', 'isPurchasable'],
            ),
        [initialEvents],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.isDark]: isDark,
                    [className]: className !== null,
                },
            ])}
        >
            {groupedByVenue
                .map(({ id, isOnline, events: groupedEvents, ...other }, index) => ({
                    id,
                    isOnline,
                    lastInTheater:
                        !isOnline &&
                        ((groupedByVenue[index + 1] || null) === null ||
                            groupedByVenue[index + 1].isOnline),
                    hasPurchaseable: (groupedEvents || []).reduce((found, evt) => {
                        const { isPurchasable = false, isUpcoming = false } = evt || {};
                        if (isPurchasable || isUpcoming) {
                            return true;
                        }
                        return found;
                    }, false),
                    events: groupedEvents,
                    ...other,
                }))
                .map(
                    (
                        { id, venue = null, events: venueEvents, lastInTheater, hasPurchaseable },
                        index,
                    ) => (
                        <Fragment key={`section-${id}-${index + 1}`}>
                            <div className={styles.item}>
                                <h3
                                    className={classNames([
                                        styles.heading,
                                        {
                                            [styles.notPurchasable]: !hasPurchaseable,
                                        },
                                    ])}
                                >
                                    {venue !== null ? (
                                        venue.name
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Online"
                                            description="events heading"
                                        />
                                    )}
                                </h3>
                                {venueEvents.map((event, idx) => {
                                    const {
                                        startDate = null,
                                        product = null,
                                        languages: eventLanguages = null,
                                        subtitleLanguages: eventSubtitleLanguages = null,
                                        isUpcoming = false,
                                        isPurchasable = false,
                                        isInvitationOnly = false,
                                        ticketsUrl = null,
                                        film = null,
                                        filmBundle = null,
                                        qa = null,
                                        presentation = null,
                                    } = event || {};

                                    const {
                                        languages: filmLanguages = null,
                                        subtitleLanguages: filmSubtitleLanguages = null,
                                    } = film || {};

                                    const {
                                        price = null,
                                        type: productType,
                                        isBundle = false,
                                        name: productName = null,
                                        description: productDescription = null,
                                    } = product || {};

                                    const { title: filmBundleTitle = null } = filmBundle || {};
                                    const languages = eventLanguages || filmLanguages || [];
                                    const subtitleLanguages =
                                        eventSubtitleLanguages || filmSubtitleLanguages || [];
                                    const isShift72Bundle = productType === 'shift72' && isBundle;
                                    const bundleTitle = isShift72Bundle
                                        ? productName
                                        : filmBundleTitle;

                                    return (
                                        <div
                                            key={`${startDate}-${idx + 1}`}
                                            className={classNames([
                                                styles.event,
                                                {
                                                    [styles.isNotPurchasable]: !isPurchasable,
                                                },
                                            ])}
                                        >
                                            <div className={styles.eventDetails}>
                                                {bundleTitle !== null ? (
                                                    <div className={styles.eventInfo}>
                                                        <h4>{bundleTitle}</h4>
                                                    </div>
                                                ) : null}

                                                <EventDates
                                                    item={event}
                                                    itemClassName={styles.eventInfo}
                                                    dateClassName={styles.info}
                                                />

                                                {isInvitationOnly === true ? (
                                                    <div className={styles.strong}>
                                                        <FormattedMessage
                                                            defaultMessage="By invitation only"
                                                            description="Tag label"
                                                        />
                                                    </div>
                                                ) : null}

                                                {languages.length > 0 ||
                                                subtitleLanguages.length > 0 ? (
                                                    <div
                                                        className={classNames(
                                                            styles.eventInfo,
                                                            styles.languages,
                                                        )}
                                                    >
                                                        {languages.length > 0 ? (
                                                            <div>
                                                                <FormattedMessage
                                                                    defaultMessage="{count, plural, one {Language:} other {Languages:}} <b>{languages}</b>"
                                                                    description="Event info label"
                                                                    values={{
                                                                        b: (chunk) => (
                                                                            <strong>{chunk}</strong>
                                                                        ),
                                                                        count: languages.length,
                                                                        languages: (languages || [])
                                                                            .map(
                                                                                ({ label }) =>
                                                                                    label,
                                                                            )
                                                                            .join(', '),
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : null}
                                                        {subtitleLanguages.length > 0 ? (
                                                            <div>
                                                                <FormattedMessage
                                                                    defaultMessage="{count, plural, one {Subtitle:} other {Subtitles:}} <b>{languages}</b>"
                                                                    description="Event info label"
                                                                    values={{
                                                                        b: (chunk) => (
                                                                            <strong>{chunk}</strong>
                                                                        ),
                                                                        count: subtitleLanguages.length,
                                                                        languages: (
                                                                            subtitleLanguages || []
                                                                        )
                                                                            .map(
                                                                                ({ label }) =>
                                                                                    label,
                                                                            )
                                                                            .join(', '),
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                                {qa !== null || presentation !== null ? (
                                                    <div
                                                        className={classNames(
                                                            styles.eventInfo,
                                                            styles.languages,
                                                        )}
                                                    >
                                                        {presentation !== null ? (
                                                            <div>
                                                                <FormattedMessage
                                                                    defaultMessage="With presentation"
                                                                    description="Event info label"
                                                                />
                                                            </div>
                                                        ) : null}
                                                        {qa !== null ? (
                                                            <div>
                                                                <FormattedMessage
                                                                    defaultMessage="With Q&A"
                                                                    description="Event info label"
                                                                />
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                                {isPurchasable &&
                                                productType === 'ticketpro' &&
                                                price !== null ? (
                                                    <div className={styles.eventInfo}>
                                                        <FormattedMessage
                                                            defaultMessage="Regular price:"
                                                            description="Price label"
                                                        />{' '}
                                                        <strong className={styles.info}>
                                                            {price}
                                                        </strong>
                                                    </div>
                                                ) : null}

                                                {isPurchasable && productDescription !== null ? (
                                                    <Body
                                                        className={classNames(
                                                            styles.eventInfo,
                                                            styles.description,
                                                        )}
                                                        body={productDescription}
                                                    />
                                                ) : null}

                                                {isPurchasable &&
                                                isBundle &&
                                                ticketsRoute !== null ? (
                                                    <Link
                                                        className={styles.tickets}
                                                        href={ticketsRoute}
                                                    >
                                                        <FormattedMessage
                                                            defaultMessage="See included films"
                                                            description="Price label"
                                                        />
                                                    </Link>
                                                ) : null}
                                            </div>
                                            {isUpcoming ? (
                                                <div className={styles.actions}>
                                                    {isPurchasable && product !== null ? (
                                                        <EventTicketsButton
                                                            item={event}
                                                            className={styles.actionButton}
                                                            iconClassName={styles.actionButtonIcon}
                                                            isDark={buttonIsDark}
                                                        />
                                                    ) : null}
                                                    {isPurchasable &&
                                                    ticketsUrl !== null &&
                                                    product === null ? (
                                                        <IconLink
                                                            className={styles.actionButton}
                                                            icon={<SmallTicketIcon />}
                                                            isDark={buttonIsDark}
                                                            href={ticketsUrl}
                                                            secondary
                                                            external
                                                        >
                                                            <FormattedMessage
                                                                defaultMessage="Buy tickets"
                                                                description="Button label"
                                                            />
                                                        </IconLink>
                                                    ) : null}
                                                    {isUpcoming ? (
                                                        <AddToFavoritesButton
                                                            className={classNames(
                                                                styles.actionButton,
                                                            )}
                                                            iconClassName={styles.actionButtonIcon}
                                                            item={event}
                                                            secondary
                                                            isDark={buttonIsDark}
                                                        />
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                            {!isExplore &&
                            !isVirtualReality &&
                            lastInTheater &&
                            hasEditionTickets &&
                            editionTickets.length > 0 ? (
                                <div className={styles.item}>
                                    <h3 className={styles.heading}>
                                        <FormattedMessage
                                            defaultMessage="Passports"
                                            description="Section heading"
                                        />
                                    </h3>
                                    {editionTickets.map(({ name, price, url }, idx) => (
                                        <div
                                            key={`${name}-${idx + 1}`}
                                            className={classNames([styles.event, styles.centered])}
                                        >
                                            <div
                                                className={classNames([
                                                    styles.eventDetails,
                                                    styles.row,
                                                ])}
                                            >
                                                <div
                                                    className={classNames([
                                                        styles.eventInfo,
                                                        styles.grow,
                                                    ])}
                                                >
                                                    <strong>{name}</strong>
                                                </div>
                                                {price !== null ? (
                                                    <div className={styles.eventInfo}>{price}</div>
                                                ) : null}
                                            </div>
                                            <div className={styles.actions}>
                                                <IconLink
                                                    className={styles.actionButton}
                                                    icon={<SmallTicketIcon />}
                                                    isDark={isDark}
                                                    href={url}
                                                    external
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Buy"
                                                        description="Button label"
                                                    />
                                                </IconLink>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </Fragment>
                    ),
                )}
        </div>
    );
};
BookingDetails.propTypes = propTypes;
BookingDetails.defaultProps = defaultProps;

export default BookingDetails;
